import { z } from 'zod'
import type { PaymentDestination, PaymentDestinationBody } from './bankAcccountModel'
import type { Company, Guarantor } from './companyModel'
import { guarantorOptions } from './companyModel'
import type { Invoice, InvoiceGateway, UpdateAttachments } from './invoiceModels'
import type { Currency } from './moneyModel'
import { currencies } from './moneyModel'
import type { QuoteOfferGateway } from './quoteModels'
import type { Proof } from './proofModels'
import { toProofDraftsSchema } from './proofModels'
import type { Receivable } from './receivableModels'
import type { Root } from './rootModel'
import type { Attachment, AttachmentGateway } from './uploadModels'
import type { User } from './userModel'
import { PaymentDestinationSchema } from './userModel'

export const billingDocumentTypes = ['invoice', 'receivable'] as const
export type BillingDocumentType = (typeof billingDocumentTypes)[number]

export function isBillingDocumentType(value: unknown): value is BillingDocumentType {
    if (typeof value !== 'string') return false
    return billingDocumentTypes.some((type) => type === value)
}

export const loanStatuses = [
    'CREATED',
    'BLOCKED',
    'ACCEPTED',
    'REFUSED',
    'PENDING_REFUSED',
    'PENDING_ACCEPTED',
    'PAYMENT_ORDERED',
    'PAYMENT_FAILED',
    'PAYMENT_RETRY_REQUESTED',
    'PAYMENT_CANCELED',
    'TO_REPAY',
    'PAST_DUE',
    'REPAID',
    'CANCELED'
] as const
export type LoanStatus = (typeof loanStatuses)[number]

export const unpaidLoansStatus: LoanStatus[] = [
    'CREATED',
    'PENDING_ACCEPTED',
    'PENDING_REFUSED',
    'REFUSED',
    'BLOCKED',
    'CANCELED'
]

export const STATUSES_EXEMPT_FROM_INVOICE_REQUIREMENT: LoanStatus[] = [
    'CANCELED',
    'PAYMENT_CANCELED',
    'REFUSED',
    'PENDING_REFUSED'
]

export type LoanStatusForm = LoanStatus | 'UNSET'
export const editableLoanStatuses: LoanStatus[] = ['CREATED', 'BLOCKED']
export const uneditableLoanStatuses = loanStatuses.filter((status) => !editableLoanStatuses.includes(status))
export const deletableLoanStatuses: LoanStatus[] = ['CREATED']

export const loanStatusesNotToDisplay = ['PENDING_REFUSED', 'PENDING_ACCEPTED']
export type LoanStatusToDisplay = Exclude<LoanStatus, 'PENDING_REFUSED' | 'PENDING_ACCEPTED'>
export const loanStatusesToDisplay = loanStatuses.filter(
    (status) => !loanStatusesNotToDisplay.includes(status)
    // eslint-disable-next-line typescript/no-unsafe-type-assertion -- the filter keeps the statuses the display list names
) as LoanStatusToDisplay[]

export type LoanRefusalReason = { code: LoanRefusalReasonCode; description?: string }

// makalu-api models/Loan.ts `cwLoanToMakaluLoan`. It drops the null fields, except the three ids below.
export type LoanGateway = Root & {
    receivableId: Receivable['id'] | null
    invoiceId: Invoice['id'] | null
    companyId: Company['id']
    userId: User['id']
    externalId?: Root['id']
    quoteOfferId?: QuoteOfferGateway['id'] | null
    amount: number
    currency?: Currency
    attachments?: AttachmentGateway[]
    payoutLabel?: string
    paidAt?: string
    repaidAt?: string
    // Set only when the route includes the payment destination of the beneficiary.
    paymentDestination?: PaymentDestination
    paymentOrderedAt?: string
    paymentFailureReasons?: PaymentFailureReasons
    paymentReconciliationId: string | null
    status: LoanStatus
    repaymentReference?: string
    invoice?: InvoiceGateway
    activeBlockReasons?: Pick<LoanBlockReason, 'code' | 'description'>[] | null
    refusalReasons?: LoanRefusalReason[]
    preferInstantPayment?: boolean | null
    guarantor?: Guarantor
}

export type Loan = Omit<LoanGateway, 'attachments' | 'invoice'> & {
    invoice?: Invoice
    attachments?: Attachment[] | undefined
    attachmentCount?: number
    isEditable?: boolean
}

// makalu-api api/loans/schemas/CreateLoanBody.ts
export type CreateLoanGateway = {
    preferInstantPayment?: boolean | null
    receivableId?: Receivable['id']
    invoiceId?: Invoice['id']
    userId: User['id']
    amount: number
    currency: Currency
    payoutLabel?: string
    guarantor?: Guarantor
    /** Deprecated by the API, which rejects it alongside `proofIds`. */
    attachments?: AttachmentGateway[]
    /** Proofs created beforehand with no target. Creation only. */
    proofIds?: Proof['id'][]
    paymentDestination?: PaymentDestinationBody
}

export type UpdateLoanAttachments = UpdateAttachments<Loan>

// makalu-api api/loans/schemas/EditLoanBody.ts makes every field of the create body optional.
export type UpdateLoanGateway = Partial<CreateLoanGateway>

export type CancelLoan = {
    loanId: Loan['id'] | undefined
    companyId: Company['id'] | undefined
}

export type DeleteLoan = {
    loanId: Loan['id']
    companyId: Company['id']
}

export interface LoansExportDatesGateway {
    startDate?: string
    endDate?: string
}

// Mirrors `loanBlockReasonCodes` in packages/business/src/loan/loanCodes.ts.
export const loanBlockReasonsCodes = [
    'RECEIVABLE_AMOUNT_NOT_VALID',
    'RECEIVABLE_CURRENCY_NOT_VALID',
    'RECEIVABLE_DELIVERY_DATE_NOT_VALID',
    'RECEIVABLE_EXPECTED_INVOICE_DATE',
    'RECEIVABLE_INFORMATION_RISK_ISSUE',
    'RECEIVABLE_ATTACHMENT_MISSING',
    'RECEIVABLE_NOT_VALID',
    'RECEIVABLE_REFERENCE_MISSING',
    'RECEIVABLE_REFERENCE_NOT_VALID',
    'RECEIVABLE_PAYMENT_TERMS_NOT_VALID',
    'DEBTOR_CONTACT_NOT_VALID',
    'DEBTOR_CONTACT_NOT_VALID_PHONE_NUMBER',
    'DEBTOR_CONTACT_NOT_VALID_POSITION',
    'DEBTOR_CONTACT_NOT_VALID_MAIL',
    'DEBTOR_CONTACT_MISSING',
    'DEBTOR_CONTACT_MISSING_PHONE_NUMBER',
    'DEBTOR_CONTACT_MISSING_POSITION',
    'DEBTOR_CONTACT_MISSING_MAIL',
    'DEBTOR_NOT_VALID',
    'DEBTOR_IDENTIFIER_NOT_VALID',
    'DEBTOR_NOT_VALID_NAME',
    'DEBTOR_RISK_ISSUE',
    'LOAN_ATTACHMENTS_NOT_VALID',
    'LOAN_ATTACHMENTS_MISSING',
    'LOAN_INFORMATION_NOT_VALID',
    'LOAN_INFORMATION_NOT_VALID_CURRENCY',
    'LOAN_AMOUNT_NOT_VALID',
    'LOAN_INFORMATION_UNUSUAL_AMOUNT',
    'AWAITING_REPAYMENTS',
    'DEBTOR_INELIGIBLE',
    'CREDIT_LIMIT_MAX_OUTSTANDING_AMOUNT_OVERPASSED',
    'CREDIT_LIMIT_MAX_OUTSTANDING_AMOUNT_NOT_SUFFICIENT',
    'CREDIT_LIMIT_MISSING',
    'GUARANTOR_CONTRACT_NOT_SIGNED',
    'BENEFICIARY_INFORMATION_NOT_VALID',
    'BENEFICIARY_INFORMATION_NOT_VALID_PHONE',
    'BENEFICIARY_INFORMATION_NOT_VALID_MAIL',
    'BENEFICIARY_INFORMATION_MISSING',
    'BENEFICIARY_INFORMATION_MISSING_PHONE',
    'BENEFICIARY_INFORMATION_MISSING_MAIL',
    'BENEFICIARY_CONTRACT_NOT_SIGNED',
    'BENEFICIARY_INFORMATION_RISK_ISSUE',
    'BENEFICIARY_KYC_NOT_VALID',
    'BENEFICIARY_KYC_MISSING',
    'BENEFICIARY_KYC_CHECK_FAILED',
    'BENEFICIARY_LIMIT_MAX_OUTSTANDING_AMOUNT_OVERPASSED',
    'INVOICE_NOT_VALID',
    'INVOICE_AMOUNT_NOT_VALID',
    'INVOICE_DATE_NOT_VALID',
    'INVOICE_NUMBER_NOT_VALID',
    'INVOICE_NUMBER_MISSING',
    'INVOICE_REPAYMENT_PERIOD_NOT_VALID',
    'INVOICE_CURRENCY_NOT_VALID',
    'INVOICE_UNUSUAL_AMOUNT',
    'INVOICE_VALIDATION_NOT_VALID',
    'INVOICE_INFORMATION_RISK_ISSUE',
    'ARIA_SERVICE_PAYMENT_PAST_DUE',
    'INVOICE_MISSING',
    'RECEIVABLE_UNUSUAL_AMOUNT',
    'USER_LIMIT_MAX_RESERVED_AMOUNT_EXCEEDED',
    'USER_LIMIT_REFUSED',
    'OTHER_REASON'
] as const
export type LoanBlockReasonCodes = (typeof loanBlockReasonsCodes)[number]

// The backend `LoanBlockReasonSchema` in domains/credit makes the description nullable.
export interface LoanBlockReason {
    code: LoanBlockReasonCodes
    description?: string | null
}

// Mirrors `loanRefusalReasonCodes` in packages/business/src/loan/loanCodes.ts.
export const loanRefusalReasons = [
    'RECEIVABLE_AMOUNT_NOT_VALID',
    'RECEIVABLE_CURRENCY_NOT_VALID',
    'RECEIVABLE_DELIVERY_DATE_NOT_VALID',
    'RECEIVABLE_EXPECTED_INVOICE_DATE',
    'RECEIVABLE_DELIVERY_DATE_TOO_OLD',
    'RECEIVABLE_INFORMATION_RISK_ISSUE',
    'RECEIVABLE_ATTACHMENT_MISSING',
    'RECEIVABLE_NOT_VALID',
    'RECEIVABLE_REFERENCE_MISSING',
    'RECEIVABLE_REFERENCE_NOT_VALID',
    'RECEIVABLE_PAYMENT_TERMS_NOT_VALID',
    'RECEIVABLE_UNUSUAL_AMOUNT',
    'ARIA_SERVICE_PAYMENT_PAST_DUE',
    'AWAITING_REPAYMENTS',
    'BENEFICIARY_CONTRACT_NOT_SIGNED',
    'BENEFICIARY_INFORMATION_MISSING',
    'BENEFICIARY_INFORMATION_MISSING_MAIL',
    'BENEFICIARY_INFORMATION_MISSING_PHONE',
    'BENEFICIARY_INFORMATION_NOT_VALID',
    'BENEFICIARY_INFORMATION_NOT_VALID_MAIL',
    'BENEFICIARY_INFORMATION_NOT_VALID_PHONE',
    'BENEFICIARY_INFORMATION_RISK_ISSUE',
    'BENEFICIARY_KYC_CHECK_FAILED',
    'BENEFICIARY_KYC_MISSING',
    'BENEFICIARY_LIMIT_MAX_OUTSTANDING_AMOUNT_OVERPASSED',
    'CREDIT_LIMIT_MAX_OUTSTANDING_AMOUNT_NOT_SUFFICIENT',
    'CREDIT_LIMIT_MISSING',
    'DEBTOR_CONTACT_MISSING',
    'DEBTOR_CONTACT_MISSING_MAIL',
    'DEBTOR_CONTACT_MISSING_PHONE_NUMBER',
    'DEBTOR_CONTACT_MISSING_POSITION',
    'DEBTOR_CONTACT_NOT_VALID',
    'DEBTOR_CONTACT_NOT_VALID_MAIL',
    'DEBTOR_CONTACT_NOT_VALID_PHONE_NUMBER',
    'DEBTOR_CONTACT_NOT_VALID_POSITION',
    'DEBTOR_IDENTIFIER_NOT_VALID',
    'DEBTOR_NOT_VALID',
    'DEBTOR_NOT_VALID_NAME',
    'DEBTOR_RISK_ISSUE',
    'GUARANTOR_CONTRACT_NOT_SIGNED',
    'INVOICE_AMOUNT_NOT_VALID',
    'INVOICE_CURRENCY_NOT_VALID',
    'INVOICE_DATE_NOT_VALID',
    'INVOICE_INFORMATION_RISK_ISSUE',
    'INVOICE_NOT_VALID',
    'INVOICE_NUMBER_MISSING',
    'INVOICE_NUMBER_NOT_VALID',
    'INVOICE_REPAYMENT_PERIOD_NOT_VALID',
    'INVOICE_UNUSUAL_AMOUNT',
    'INVOICE_VALIDATION_NOT_VALID',
    'LOAN_AMOUNT_NOT_VALID',
    'LOAN_ATTACHMENTS_MISSING',
    'LOAN_ATTACHMENTS_NOT_VALID',
    'LOAN_INFORMATION_NOT_VALID',
    'LOAN_INFORMATION_NOT_VALID_CURRENCY',
    'LOAN_INFORMATION_UNUSUAL_AMOUNT',
    'BENEFICIARY_KYC_NOT_VALID',
    'BENEFICIARY_NOT_ELIGIBLE',
    'CREDIT_LIMIT_MAX_OUTSTANDING_AMOUNT_OVERPASSED',
    'CREDIT_LIMIT_REFUSED',
    'DEBTOR_NOT_ELIGIBLE',
    'INVOICE_TOO_OLD',
    'INVOICE_MISSING',
    'LATE_DEBTOR_REFUND',
    'LOAN_CREATION_DATE_TOO_CLOSE_OR_EXCEEDS_INVOICE_DUE_DATE',
    'OTHER_REASON',
    'QUOTE_OFFER_EXPIRED',
    'QUOTE_REFUSED',
    'USER_LIMIT_MAX_RESERVED_AMOUNT_EXCEEDED',
    'USER_LIMIT_REFUSED'
] as const
export type LoanRefusalReasonCode = (typeof loanRefusalReasons)[number]

export const paymentFailureReasonCodes = ['OTHER_REASON', 'PAYMENT_DESTINATION_NOT_VALID'] as const
export type PaymentFailureReasonCode = (typeof paymentFailureReasonCodes)[number]
export type PaymentFailureReasons = { code: PaymentFailureReasonCode }[]

export type CreatePaymentRetry = {
    loanId: Loan['id']
    paymentDestination: Omit<PaymentDestination, 'routingNumberType'>
    preferInstantPayment: boolean
}

export type CreatePaymentRetryGateway = {
    options: {
        preferInstantPayment: boolean
        paymentDestination: PaymentDestinationBody
    }
}

export const BaseLoanFormSchema = z.object({
    invoiceId: z.string().optional(),
    receivableId: z.string().optional(),
    userId: z.string().min(1),
    currency: z.enum(currencies),
    amount: z.number().min(0.01),
    guarantor: z.enum(guarantorOptions).optional(),
    paymentDestination: PaymentDestinationSchema.nullable(),
    payoutLabel: z.string().optional(),
    attachments: typeof File !== 'undefined' ? z.array(z.instanceof(File)).optional() : z.array(z.any()).optional(),
    /** The typed documents of a new loan, while the proof upload is on. A loan is never gated on its documents. */
    proofs: toProofDraftsSchema({ requiresOneDocument: false }).optional()
})

// API Schema (number amount)
export const BaseLoanPayloadSchema = BaseLoanFormSchema.extend({
    amount: z.number().min(0.01),
    paymentDestination: PaymentDestinationSchema.omit({ type: true }).nullable()
})

// Create schemas

// Update form (all fields optional except loanId)
export const UpdateLoanFormSchema = BaseLoanFormSchema.extend({
    loanId: z.string(),
    paymentDestination: PaymentDestinationSchema.omit({ type: true }).nullable()
})
    .partial()
    .required({ loanId: true })

export const UpdateLoanPayloadSchema = BaseLoanPayloadSchema.partial().extend({
    loanId: z.string()
})
export type CreateLoanForm = z.infer<typeof BaseLoanFormSchema>
export type CreateLoanPayload = z.infer<typeof BaseLoanPayloadSchema>
export type UpdateLoanForm = z.infer<typeof UpdateLoanFormSchema>
export type UpdateLoanPayload = z.infer<typeof UpdateLoanPayloadSchema>
